import React, { useRef, useImperativeHandle, useEffect } from "react";
import styled from "styled-components";

const InputTextStyle = styled.div`
    width: 100%;
`;

const ContainerStyle = styled.div`
    height: 26px;
    display: flex;
    border-radius: 2px;
    box-sizing: border-box;
    background-color: ${props => props.theme.white};
    border: 1px solid ${props => props.theme.greyLight};
    &:focus-within {
        border-color: ${props => props.theme.white};
        outline: 1px solid ${props => props.theme.primaryLight};
    }
    &.disabled {
        pointer-events: none;
        background-color: ${props => props.theme.greyLighter};
    }
    &.error {
        background-color: ${props => props.theme.redLight};
        border-color: ${props => props.theme.redDark};
        &:focus-within {
            border-color: ${props => props.theme.redLight};
            outline: 1px solid ${props => props.theme.redDarker};
        }
    }
`;

const TitleStyle = styled.p`
    height: 12px;
    font-size: 12px;
    user-select: none;
    margin-bottom: 1px;
    letter-spacing: 0.4px;
    color: ${props => props.theme.black};
`;

const InputStyle = styled.input`
    width: 100%;
    border: none;
    display: block;
    font-weight: 400;
    box-sizing: border-box;
    padding: 3px 5px 2px 10px;
    background-color: transparent;
    color: ${props => props.theme.black};
    text-align: ${props => props.$textAlign};
    &::placeholder {
        color: ${props => props.theme.greyLight};
    }
`;

const ErrorStyle = styled.p`
    height: 0;
    opacity: 0;
    font-size: 11px;
    visibility: hidden;
    color: ${props => props.theme.redDark};
    transition: opacity 0.3s, height 0.3s, visibility 0.3s;
    &.error {
        opacity: 1;
        height: 11px;
        margin-top: 2px;
        visibility: visible;
    }
`;

export const InputText = props => {

    const { id, ref, name, title, value, disabled, maxLength, placeholder, password, textAlign = "left", error, errorVisible, transform } = props;

    const inputRef = useRef(null);

    const errorName = name ? name + "ErrorText" : "errorText";

    const disabledClassName = disabled ? "disabled" : "";
    const errorClassName = (errorVisible && error) ? "error" : "";
    const className = [disabledClassName, errorClassName].join(" ");

    useImperativeHandle(ref, () => ({
        get value() {
            return inputRef.current.value;
        },
        focus: () => inputRef.current.focus(),
        writeValue: val => (inputRef.current.value = toString(val)),
        addEventListener: (type, listener) => inputRef.current.addEventListener(type, listener),
        removeEventListener: (type, listener) => inputRef.current.removeEventListener(type, listener)
    }), [inputRef]);

    useEffect(() => {
        if (value !== toValue(inputRef.current.value)) {
            if (document.activeElement !== inputRef.current) {
                inputRef.current.value = toString(value);
            }
        }
    }, [value]);

    const onKeyDown = event => {
        if (props.onKeyDown) {
            props.onKeyDown(event);
        }
    };

    const isBlank = value => (value === null || value === undefined || /^\s*$/.test(value));
    const toString = value => (isBlank(value) ? "" : value);
    const toValue = value => (isBlank(value) ? null : value);

    const onChange = event => {
        const value = toValue(event.target.value);
        if (transform) {
            const transformed = transform(value);
            if (transformed !== value) {
                const { selectionStart, selectionEnd } = event.target;
                inputRef.current.value = transformed ?? "";
                inputRef.current.setSelectionRange(selectionStart, selectionEnd);
            }
            props.onChange(transformed);
        } else {
            props.onChange(value);
        }
    };

    return (
        <InputTextStyle>
            {title !== false &&
                <TitleStyle>{title}</TitleStyle>
            }
            <ContainerStyle className={className}>
                <InputStyle
                    id={id}
                    name={name}
                    ref={inputRef}
                    onChange={onChange}
                    disabled={disabled}
                    maxLength={maxLength}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    className={errorClassName}
                    type={password ? "password" : "text"}
                    $textAlign={textAlign} />
            </ContainerStyle>
            <ErrorStyle
                name={errorName}
                className={typeof error === "string" ? errorClassName : ""}>
                {error}
            </ErrorStyle>
        </InputTextStyle>
    );
};